import type { Episode } from "@/types/episode";

interface PlayerPanelProps {
  episode: Episode;
  noticeOpen: boolean;
  onOpenNotice: () => void;
  onCloseNotice: () => void;
}

export default function PlayerPanel({
  episode,
  noticeOpen,
  onOpenNotice,
  onCloseNotice,
}: PlayerPanelProps) {
  return (
    <section aria-label="演示播放区">
      <div className="rounded-lg overflow-hidden border border-background-200/70 bg-background-950">
        <div className="relative w-full aspect-video">
          <img
            src={episode.cover}
            alt={`${episode.title} 播放画面示意`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/50 to-background-950/30" />

          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <button
              type="button"
              onClick={onOpenNotice}
              aria-label="播放演示画面"
              className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-3xl md:text-4xl ml-0.5" />
            </button>
            <p className="mt-4 text-xs md:text-sm text-foreground-700">
              {episode.title} · {episode.duration}
            </p>
            <span className="mt-2 inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-background-950/70 border border-background-300/50 text-[11px] text-foreground-500 backdrop-blur">
              <i className="ri-error-warning-line text-accent-500" />
              演示播放器 · 当前版本不支持真实播放
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 px-3 md:px-4 pb-3 pt-8 bg-gradient-to-t from-background-950 to-transparent">
            <div className="h-1 rounded-full bg-background-700/40 overflow-hidden">
              <div className="h-full w-[18%] bg-primary-500" />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-600">
              <div className="flex items-center gap-3">
                <i className="ri-play-circle-line text-base text-foreground-500" />
                <i className="ri-volume-up-line text-base text-foreground-500" />
                <span>08:12 / {episode.duration}</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-hd-line text-base text-foreground-500" />
                <i className="ri-fullscreen-line text-base text-foreground-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-2.5 text-[11px] leading-5 text-foreground-600 flex items-start gap-1.5">
        <i className="ri-information-line text-accent-500 mt-0.5" />
        播放区域为前端静态演示界面，未接入任何真实视频源，点击播放仅显示提示说明。
      </p>

      {noticeOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭提示"
            onClick={onCloseNotice}
            className="absolute inset-0 bg-background-950/80 backdrop-blur-sm cursor-pointer"
          />
          <div className="relative w-full max-w-md rounded-lg bg-background-100 border border-background-200 p-6 anim-fade-up">
            <div className="w-11 h-11 rounded-full bg-primary-500/15 text-primary-400 flex items-center justify-center">
              <i className="ri-play-circle-line text-xl" />
            </div>
            <h3 className="mt-4 font-heading text-lg text-foreground-950">演示播放提示</h3>
            <p className="mt-2 text-sm leading-7 text-foreground-500">
              当前为页面演示版本，播放器未接入真实视频接口，因此无法实际播放
              {episode.title}。你仍可继续浏览本集的剧集信息、演员表与完整剧情介绍。
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onCloseNotice}
                className="h-9 px-4 rounded-md bg-background-200/70 hover:bg-background-300/60 text-xs text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}